/**
 * 맥스매치 운영 시간 — 레거시 `maxMatchOperatingHours.js` 포팅.
 * 좌측: ECS 서버 동작 시간(조회 전용) / 우측: 화면별 노출 시간.
 */
import {
  useMaxMatchOperatingSettings,
  useSaveMaxMatchScreenWindows,
} from '@/entities/max-match/hooks/useMaxMatch';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { DataCard } from '@/shared/components/DataCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { TimePicker } from '@/shared/components/TimePicker';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { CardContent } from '@/shared/components/ui/card';
import { Label } from '@/shared/components/ui/label';
import { Separator } from '@/shared/components/ui/separator';
import {
  clampScreenSettingsToServerOperation,
  DEFAULT_SERVER_OPERATION,
  getChangedScreenTypes,
  getOperatingHoursSaveErrorMessage,
  INITIAL_SCREEN_SETTINGS,
  mapOperatingHoursResponse,
  mapScreenWindowsResponse,
  MAX_MATCH_OPERATING_HOUR_UNAVAILABLE_MESSAGE,
  normalizeScreenSettings,
  SCREEN_SECTION_KEY,
  SCREEN_SECTION_LABEL,
  toScreenWindowUpsertPayload,
  validateChangedScreenSettings,
  validateScreenSectionWithinServerOperation,
  WEEKDAYS,
  type ScreenSection,
  type ScreenSettings,
  type ServerOperation,
} from '@/pages/max-match/maxMatchOperatingHoursMapper';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useBlocker } from '@tanstack/react-router';
import { Save, Sparkles } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { ToolbarRow } from '@/shared/components/ui/row';

const ServerOperationStatus: React.FC<{ available: boolean }> = ({
  available,
}) =>
  available ? (
    <Badge variant="success">운영중</Badge>
  ) : (
    <Badge variant="secondary">확인 불가</Badge>
  );

const TimeRangeFields: React.FC<{
  startTime: string;
  endTime: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onStartChange?: (value: string) => void;
  onEndChange?: (value: string) => void;
}> = ({
  startTime,
  endTime,
  min,
  max,
  disabled,
  readOnly,
  onStartChange,
  onEndChange,
}) => (
  <div className="flex flex-wrap items-end gap-3">
    <div className="flex flex-col gap-1">
      <Label>시작</Label>
      <TimePicker
        value={startTime}
        min={min}
        max={max}
        disabled={disabled}
        readOnly={readOnly}
        onChange={onStartChange}
        className="w-32"
      />
    </div>
    <span className="pb-2 text-sm text-muted-foreground" aria-hidden>
      ~
    </span>
    <div className="flex flex-col gap-1">
      <Label>종료</Label>
      <TimePicker
        value={endTime}
        min={min}
        max={max}
        disabled={disabled}
        readOnly={readOnly}
        onChange={onEndChange}
        className="w-32"
      />
    </div>
  </div>
);

const ScreenSectionEditor: React.FC<{
  title: string;
  section: ScreenSection;
  serverOperation: ServerOperation;
  editable: boolean;
  onChange: (patch: Partial<ScreenSection>) => void;
}> = ({ title, section, serverOperation, editable, onChange }) => (
  <section className="space-y-3">
    <h3 className="text-base font-semibold text-foreground">{title}</h3>
    <TimeRangeFields
      startTime={section.startTime}
      endTime={section.endTime}
      min={serverOperation.startTime || undefined}
      max={serverOperation.endTime || undefined}
      disabled={!editable}
      onStartChange={(startTime) => onChange({ startTime })}
      onEndChange={(endTime) => onChange({ endTime })}
    />
    <ToolbarRow gap={8}>
      {WEEKDAYS.map((day) => {
        const selected = section.days.includes(day.value);
        const allowed =
          serverOperation.days.length === 0 ||
          serverOperation.days.includes(day.value);
        return (
          <Button
            key={day.value}
            type="button"
            size="sm"
            variant={selected ? 'default' : 'outline'}
            disabled={!editable || !allowed}
            onClick={() => {
              const next = selected
                ? section.days.filter((d) => d !== day.value)
                : [...section.days, day.value].sort((a, b) => a - b);
              onChange({ days: next });
            }}
          >
            {day.label}
          </Button>
        );
      })}
    </ToolbarRow>
  </section>
);

export const MaxMatchOperatingHoursPage: React.FC = () => {
  const toast = useToast();
  const { readOnly } = useMenuAccess();
  const { data, isLoading, isError, error } = useMaxMatchOperatingSettings();
  const saveWindows = useSaveMaxMatchScreenWindows();

  const [serverOperation, setServerOperation] = useState<ServerOperation>(
    DEFAULT_SERVER_OPERATION,
  );
  const [screenSettings, setScreenSettings] =
    useState<ScreenSettings>(INITIAL_SCREEN_SETTINGS);
  const [initialScreenSettings, setInitialScreenSettings] =
    useState<ScreenSettings>(INITIAL_SCREEN_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

  useEffect(() => {
    if (!data) return;
    const nextServer = mapOperatingHoursResponse(data.operatingHours);
    setServerOperation(nextServer);
    const nextScreens = clampScreenSettingsToServerOperation(
      mapScreenWindowsResponse(data.screenWindows),
      nextServer,
    );
    setScreenSettings(nextScreens);
    setInitialScreenSettings(nextScreens);
    setLoaded(true);
  }, [data]);

  useEffect(() => {
    if (isError && error) handleApiError(error, toast);
  }, [isError, error, toast]);

  const editable =
    loaded &&
    !readOnly &&
    serverOperation.available &&
    serverOperation.days.length > 0;

  const isDirty = useMemo(
    () =>
      JSON.stringify(normalizeScreenSettings(screenSettings)) !==
      JSON.stringify(normalizeScreenSettings(initialScreenSettings)),
    [screenSettings, initialScreenSettings],
  );

  // 레거시 QuitModal — 나가기 버튼 없이 라우트 이탈·새로고침 시 가드
  const blocker = useBlocker({
    shouldBlockFn: () => isDirty,
    enableBeforeUnload: isDirty,
    withResolver: true,
    disabled: !isDirty,
  });

  useEffect(() => {
    if (blocker.status === 'blocked') {
      setLeaveDialogOpen(true);
    }
  }, [blocker.status]);

  const tryUpdateSection = (
    sectionKey: keyof ScreenSettings,
    patch: Partial<ScreenSection>,
  ) => {
    if (!editable) {
      toast.error(MAX_MATCH_OPERATING_HOUR_UNAVAILABLE_MESSAGE);
      return;
    }
    const nextSection = { ...screenSettings[sectionKey], ...patch };
    const validation = validateScreenSectionWithinServerOperation(
      nextSection,
      serverOperation,
    );
    if (!validation.valid) {
      toast.error(validation.message ?? '유효하지 않은 설정입니다.');
      return;
    }
    setScreenSettings((prev) => ({
      ...prev,
      [sectionKey]: nextSection,
    }));
  };

  /** 레거시: 검증 통과 후 확인 모달 오픈 */
  const requestSave = () => {
    if (!editable) {
      toast.error(MAX_MATCH_OPERATING_HOUR_UNAVAILABLE_MESSAGE);
      return;
    }
    const validation = validateChangedScreenSettings(
      screenSettings,
      initialScreenSettings,
      serverOperation,
    );
    if (!validation.valid) {
      toast.error(validation.message ?? '저장할 수 없습니다.');
      return;
    }
    setSaveDialogOpen(true);
  };

  const handleSave = async () => {
    const changedTypes = getChangedScreenTypes(
      screenSettings,
      initialScreenSettings,
    );
    const requests = changedTypes.map((screenType) => ({
      screenType,
      payload: toScreenWindowUpsertPayload(
        screenSettings[SCREEN_SECTION_KEY[screenType]],
      ),
    }));
    try {
      await saveWindows.mutateAsync(requests);
      setInitialScreenSettings(screenSettings);
      setSaveDialogOpen(false);
      toast.success('운영 시간 설정을 저장했습니다.');
    } catch (e) {
      toast.error(getOperatingHoursSaveErrorMessage(e));
    }
  };

  const handleLeaveCancel = () => {
    setLeaveDialogOpen(false);
    blocker.reset?.();
  };

  const handleLeaveConfirm = () => {
    setLeaveDialogOpen(false);
    blocker.proceed?.();
  };

  const sectionKeys = Object.keys(
    SCREEN_SECTION_LABEL,
  ) as Array<keyof ScreenSettings>;

  if (isLoading) {
    return <LoadingSpinner tip="운영 시간 설정을 불러오는 중..." />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Sparkles}
        title="맥스매치 운영 시간 설정"
        description="ECS 서버 운영 시간은 조회 전용이며, 화면별 노출 시간만 수정할 수 있습니다."
        actions={
          <MenuWriteGate>
            <Button
              size="sm"
              className="ruda-button"
              onClick={requestSave}
              disabled={!isDirty || saveWindows.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              저장
            </Button>
          </MenuWriteGate>
        }
      />

      {/* 레거시: 좌측 서버 동작 시간 · 우측 화면별 설정 */}
      <DataCard>
        <CardContent className="p-5">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-12">
            <section className="w-full shrink-0 space-y-4 lg:max-w-sm">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-semibold text-foreground">
                    맥스매치 운영 서버 동작 시간
                  </h2>
                  <ServerOperationStatus
                    available={serverOperation.available}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  서버 운영 시간은 ECS 예약 기준으로 조회되며 수정할 수
                  없습니다.
                </p>
              </div>
              <TimeRangeFields
                startTime={serverOperation.startTime}
                endTime={serverOperation.endTime}
                readOnly
              />
              <ToolbarRow gap={8}>
                {WEEKDAYS.map((day) => (
                  <Button
                    key={day.value}
                    size="sm"
                    variant={
                      serverOperation.days.includes(day.value)
                        ? 'default'
                        : 'outline'
                    }
                    disabled
                  >
                    {day.label}
                  </Button>
                ))}
              </ToolbarRow>
            </section>

            <div
              className="hidden w-px shrink-0 self-stretch bg-border lg:block"
              aria-hidden
            />
            <Separator className="lg:hidden" />

            <div className="min-w-0 flex-1 space-y-5 lg:max-w-xl">
              {sectionKeys.map((sectionKey, index) => (
                <React.Fragment key={sectionKey}>
                  {index > 0 ? <Separator /> : null}
                  <ScreenSectionEditor
                    title={SCREEN_SECTION_LABEL[sectionKey]}
                    section={screenSettings[sectionKey]}
                    serverOperation={serverOperation}
                    editable={editable}
                    onChange={(patch) => tryUpdateSection(sectionKey, patch)}
                  />
                </React.Fragment>
              ))}
            </div>
          </div>
        </CardContent>
      </DataCard>

      <AlertDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>확인</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 운영 시간 설정을 저장하시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleSave()}
              disabled={saveWindows.isPending}
            >
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={leaveDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleLeaveCancel();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>나가기</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 내용이 삭제됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleLeaveCancel}>
              계속 편집
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleLeaveConfirm}>
              나가기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
